import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { auth } from '../firebase'; // db is no longer needed here for this specific logic
import { sendPasswordResetEmail } from 'firebase/auth';
// doc, getDoc are no longer needed here for this specific logic

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, currentUser } = useAuth();
  const navigate = useNavigate();

  // State for Forgot Password
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);

  // Effect for post-login navigation using custom claims
  useEffect(() => {
    if (currentUser && !loading) { 
      const fetchClaimsAndRedirect = async () => {
        try {
          const idTokenResult = await currentUser.getIdTokenResult(true); // Force refresh for latest claims
          const claims = idTokenResult.claims;
          const accessibleHotels = claims.accessibleHotels;
          const isAdmin = claims.admin === true;
          const isStaff = claims.staff === true;

          if (Array.isArray(accessibleHotels) && accessibleHotels.length === 1) {
            const hotelId = accessibleHotels[0];
            // Staff-only users go to found items registration, admins go to dashboard
            if (isStaff && !isAdmin) {
              navigate(`/${hotelId}/found-items/register`);
            } else {
              navigate(`/${hotelId}/admin`);
            }
          } else {
            // If multiple hotels, 0 hotels, or accessibleHotels not an array/undefined, go to HotelSelector
            navigate('/'); 
          }
        } catch (err) {
          console.error("Error fetching user claims for redirect:", err);
          setError("Could not retrieve user claims for navigation. Please select a hotel manually.");
          navigate('/'); // Fallback on error to HotelSelector
        }
      };
      
      // Conditions to trigger redirect
      if (!error && email && password) { // After a successful login attempt from form
          fetchClaimsAndRedirect();
      } else if (!email && !password && currentUser && !error) {
          // User is already logged in (e.g., page refresh, already had session) and no pending login error
          fetchClaimsAndRedirect();
      }

    }
  }, [currentUser, navigate, error, loading, email, password]); 

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await login(email, password);
      // Navigation is handled by useEffect. 
      // Important: setLoading(false) should ideally happen *after* currentUser is processed by useEffect
      // or if login itself provides a clear signal of completion beyond just setting currentUser.
      // For now, let's ensure loading is reset if useEffect doesn't navigate quickly.
      // A more robust solution might involve the login() promise resolving only after all context updates are certain.
    } catch (err) { 
      setError('Failed to log in: ' + err.message);
      setLoading(false); // Reset loading on login error
    } finally {
      // This finally block ensures setLoading(false) is called 
      // even if login() succeeds but before useEffect redirects.
      // However, if useEffect redirects, this component might unmount.
      // A more robust way is for useEffect to control this based on its actions.
      // Let's try setting it false here, but be mindful of race conditions with unmount.
      // A slight delay could also work if race conditions are an issue.
      setLoading(false); // This will allow the useEffect to run its !loading check
    }
  }

  async function handleForgotPassword(e) {
    e.preventDefault();
    setForgotPasswordMessage('');
    setForgotPasswordLoading(true);
    try {
      await sendPasswordResetEmail(auth, forgotPasswordEmail);
      setForgotPasswordMessage('Password reset email sent! Check your inbox.');
      setForgotPasswordEmail(''); 
    } catch (err) {
      setForgotPasswordMessage('Failed to send password reset email: ' + err.message);
    }
    setForgotPasswordLoading(false);
  }

  return (
    <div className="login-container">
      <h2>Login</h2>
      {error && <div className="error" style={{ color: 'red', marginBottom: '10px' }}>{error}</div>}

      {!showForgotPassword ? (
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <button disabled={loading} type="submit" style={{ marginRight: '10px' }}>
            {loading ? 'Logging in...' : 'Log In'}
          </button>
          <button
            type="button"
            onClick={() => { setShowForgotPassword(true); setError(''); setForgotPasswordMessage(''); }}
            disabled={loading}
            className="link-button"
          >
            Forgot Password?
          </button>
        </form>
      ) : (
        <div>
          <h3>Reset Password</h3>
          {forgotPasswordMessage &&
            <div style={{ color: forgotPasswordMessage.startsWith('Failed') ? 'red' : 'green', marginBottom: '10px' }}>
              {forgotPasswordMessage}
            </div>}
          <form onSubmit={handleForgotPassword}>
            <div className="form-group">
              <label>Enter your email address:</label>
              <input
                type="email"
                value={forgotPasswordEmail}
                onChange={(e) => setForgotPasswordEmail(e.target.value)}
                required
              />
            </div>
            <button disabled={forgotPasswordLoading} type="submit" style={{ marginRight: '10px' }}>
              {forgotPasswordLoading ? 'Sending...' : 'Send Reset Email'}
            </button>
            <button type="button" onClick={() => setShowForgotPassword(false)} disabled={forgotPasswordLoading} className="link-button">
              Back to Login
            </button>
          </form>
        </div>
      )}
    </div>
  );
} 